/**
 * #######################################################
 *
 * Fraîcheur des messages (pages MESSAGE IN / MESSAGE OUT)
 *
 * Les documents messageIN / messageOUT portent deux dates :
 *  - "datetime" : chaîne UTC lisible "YYYY-MM-DD HH:mm:ss.SSS"
 *  - "ts"       : timestamp epoch en millisecondes (UTC lui aussi)
 *
 * Toute comparaison se fait sur "ts" : c'est un nombre absolu, donc
 * insensible au fuseau du navigateur comme à celui du serveur. La chaîne
 * "datetime" n'est utilisée qu'en repli, et elle est alors explicitement
 * interprétée comme de l'UTC (sans le "Z" final, JS la lirait en heure locale).
 *
 * #######################################################
 */
import lod_ from "lodash";

/**
 * Durée d'une minute en millisecondes
 */
const ONE_MINUTE_MS = 60 * 1000;

/**
 * Seuils de fraîcheur, du plus récent au plus ancien.
 * - fresh   : moins de 5 minutes          -> vert
 * - warning : entre 5 et 10 minutes       -> orange
 * - stale   : plus de 10 minutes          -> rouge
 *
 * Les bornes sont exprimées en âge (now - ts) et sont contiguës :
 * [0, 5min[ , [5min, 10min[ , [10min, +inf[
 */
export const FRESHNESS_LEVELS = [
	{
		code: "fresh",
		color: "success",
		hexColor: "#2e7d32",
		icon: "check_circle",
		minAgeMs: 0,
		maxAgeMs: 5 * ONE_MINUTE_MS
	},
	{
		code: "warning",
		color: "warning",
		hexColor: "#ed6c02",
		icon: "warning",
		minAgeMs: 5 * ONE_MINUTE_MS,
		maxAgeMs: 10 * ONE_MINUTE_MS
	},
	{
		code: "stale",
		color: "error",
		hexColor: "#d32f2f",
		icon: "error",
		minAgeMs: 10 * ONE_MINUTE_MS,
		maxAgeMs: null
	}
];

/**
 * Attribut de la collection portant le timestamp epoch
 */
export const FRESHNESS_ATTRIBUTE = "ts";

/**
 * Collections auxquelles la fraîcheur s'applique
 */
export const FRESHNESS_COLLECTIONS = ["messageIN", "messageOUT"];

/**
 * Interprète la chaîne "datetime" d'un message comme de l'UTC.
 * Format attendu : "YYYY-MM-DD HH:mm:ss.SSS"
 * @param {String} datetime la chaîne stockée en base
 * @returns {Number|null} le timestamp epoch en ms, ou null si illisible
 */
export function parseUtcDatetime(datetime) {
	if (!lod_.isString(datetime)) return null;

	const match = datetime.match(
		/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?/
	);

	if (!match) return null;

	const [, year, month, day, hour, minute, second, millisecond] = match;

	return Date.UTC(
		Number(year),
		Number(month) - 1,
		Number(day),
		Number(hour),
		Number(minute),
		Number(second),
		Number(millisecond ?? 0)
	);
}

/**
 * Récupère le timestamp epoch d'une ligne de la table.
 * Priorité à "ts" (déjà un nombre), repli sur "datetime" lu en UTC.
 * @param {Object} row la ligne brute renvoyée par le back
 * @returns {Number|null} le timestamp epoch en ms, ou null si indisponible
 */
export function getMessageTimestamp(row) {
	if (lod_.isNil(row)) return null;

	const ts = row[FRESHNESS_ATTRIBUTE];

	if (lod_.isNumber(ts)) return ts;
	if (lod_.isString(ts) && ts.trim() !== "" && !Number.isNaN(Number(ts))) return Number(ts);

	return parseUtcDatetime(row.datetime);
}

/**
 * Détermine le niveau de fraîcheur d'un timestamp.
 * @param {Number} timestamp epoch en ms
 * @param {Number} now référence temporelle, par défaut l'instant du navigateur
 * @returns {Object|null} le niveau de FRESHNESS_LEVELS, ou null si timestamp invalide
 */
export function getFreshnessLevel(timestamp, now = Date.now()) {
	if (!lod_.isNumber(timestamp) || Number.isNaN(timestamp)) return null;

	// Un message daté dans le futur (dérive d'horloge) est traité comme le plus frais
	const age = Math.max(0, now - timestamp);

	return (
		FRESHNESS_LEVELS.find(level => lod_.isNil(level.maxAgeMs) || age < level.maxAgeMs) ??
		FRESHNESS_LEVELS[FRESHNESS_LEVELS.length - 1]
	);
}

/**
 * Retourne un niveau par son code.
 * @param {String} code code du niveau
 * @returns {Object|null} le niveau, ou null s'il n'existe pas
 */
export function getFreshnessLevelByCode(code) {
	return FRESHNESS_LEVELS.find(level => level.code === code) ?? null;
}

/**
 * Construit le filtre MongoDB correspondant à un niveau de fraîcheur.
 *
 * Le filtre porte sur "ts" et les bornes sont calculées à partir de l'instant
 * du navigateur : c'est bien "now" de l'utilisateur qui fait foi, comme demandé.
 * Le back (buildPaginedListWithPagination) recopie tel quel un filtre qui n'a
 * ni "name" ni "type", donc l'objet produit ici arrive intact dans le $match.
 *
 * @param {String} code code du niveau sélectionné, null si aucun
 * @param {Number} now référence temporelle, par défaut l'instant du navigateur
 * @returns {Object} le filtre à fusionner dans la requête, {} si aucun niveau
 */
export function buildFreshnessFilter(code, now = Date.now()) {
	const level = getFreshnessLevelByCode(code);

	if (!level) return {};

	const condition = {};

	/**
	 * Les bornes doivent reproduire exactement celles de getFreshnessLevel,
	 * sinon un message pile sur un seuil serait colore dans un niveau et
	 * remonte par le filtre d'un autre.
	 * getFreshnessLevel teste "age < maxAgeMs", donc "ts > now - maxAgeMs",
	 * et "age >= minAgeMs", donc "ts <= now - minAgeMs".
	 */
	if (!lod_.isNil(level.maxAgeMs)) {
		condition.$gt = now - level.maxAgeMs;
	}
	if (level.minAgeMs > 0) {
		condition.$lte = now - level.minAgeMs;
	}

	if (lod_.isEmpty(condition)) return {};

	return { [FRESHNESS_ATTRIBUTE]: condition };
}

/**
 * Formate l'âge d'un message pour l'infobulle.
 * @param {Number} timestamp epoch en ms
 * @param {Number} now référence temporelle, par défaut l'instant du navigateur
 * @returns {String} l'âge formaté, chaîne vide si timestamp invalide
 */
export function formatMessageAge(timestamp, now = Date.now()) {
	if (!lod_.isNumber(timestamp) || Number.isNaN(timestamp)) return "";

	const ageMinutes = Math.max(0, Math.floor((now - timestamp) / ONE_MINUTE_MS));

	if (ageMinutes < 1) return "< 1 min";
	if (ageMinutes < 60) return `${ageMinutes} min`;

	const ageHours = Math.floor(ageMinutes / 60);

	if (ageHours < 24) return `${ageHours} h ${ageMinutes % 60} min`;

	return `${Math.floor(ageHours / 24)} j ${ageHours % 24} h`;
}
